import { useEffect, useRef, useState } from "react";
import { useTheme } from "../../components/portfolio/context/ThemeContext";
import {
   AI_ASSISTANT_API_URL,
   consumeStreamingResponse,
   extractAnswer,
   formatStreamStatus,
   parseMaybeJson,
} from "../../components/portfolio/ai-assistant/streamRagClient";
import AssistantMarkdown from "../../components/portfolio/ai-assistant/AssistantMarkdown";
import {
   AI_SUGGESTED_QUESTIONS,
   ASSISTANT_AVATAR,
   ASSISTANT_GREETING,
   ASSISTANT_NAME,
   PORTFOLIO_OWNER_AVATAR,
} from "../../components/portfolio/ai-assistant/aiAssistantConfig";

const AI_CAPABILITIES = [
   {
      id: "resume",
      icon: "chat",
      title: "Resume Q&A",
      desc: "Ask anything about your resume.",
      color: "purple",
   },
   {
      id: "search",
      icon: "search",
      title: "Semantic Search",
      desc: "Find the most relevant information.",
      color: "purple",
   },
   {
      id: "responses",
      icon: "chatspark",
      title: "AI Responses",
      desc: "Get intelligent, context-aware answers.",
      color: "purple",
   },
   {
      id: "vector",
      icon: "code2",
      title: "Vector Embeddings",
      desc: "Understand meaning and context.",
      color: "purple",
   },
   {
      id: "rag",
      icon: "network",
      title: "RAG Architecture",
      desc: "Retrieve the right information.",
      color: "pink",
   },
];

const QUICK_CHIPS = AI_SUGGESTED_QUESTIONS.map((q) => ({
   id: q.id,
   icon: q.chipIcon,
   label: q.text,
   description: q.description,
   color: q.color,
}));

const PROFILE = {
   name: "Sunil Rawat",
   role: "Full Stack Engineer",
   bio: "Building intelligent and scalable web applications with modern technologies.",
   linkedin: "https://www.linkedin.com/in/sunil-rawat-0059861ab/",
   github: "https://github.com/sunilrawatofficial",
   email: "mailto:sunilrawatofficial@gmail.com",
};

const PORTFOLIO_URL =
   typeof window !== "undefined" ? window.location.origin : "/";

/* ---------- inline icons (no external deps) ---------- */
function Spark({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
         <path d="M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2z" />
         <path d="M19 14l.9 2.5L22 17.5l-2.1.9L19 21l-.9-2.6L16 17.5l2.1-1L19 14z" />
      </svg>
   );
}
function Briefcase({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <rect x="3" y="7" width="18" height="13" rx="2" />
         <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      </svg>
   );
}
function CodeIcon({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <polyline points="16 18 22 12 16 6" />
         <polyline points="8 6 2 12 8 18" />
      </svg>
   );
}
function Download({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
         <polyline points="7 10 12 15 17 10" />
         <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
   );
}
function Send({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
         <path d="M2.5 11.5L21 3l-8.5 18.5-2-8L2.5 11.5z" />
      </svg>
   );
}
function LinkedIn({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
         <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.22 8h4.56v14H.22zM8.06 8h4.37v1.91h.06c.61-1.16 2.1-2.39 4.32-2.39 4.62 0 5.47 3.04 5.47 6.99V22h-4.55v-6.18c0-1.47-.03-3.36-2.05-3.36-2.05 0-2.36 1.6-2.36 3.25V22H8.06z" />
      </svg>
   );
}
function Github({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
         <path d="M12 .5C5.65.5.5 5.65.5 12c0 5.08 3.29 9.39 7.86 10.91.58.11.79-.25.79-.56v-2.16c-3.2.7-3.87-1.36-3.87-1.36-.52-1.32-1.27-1.68-1.27-1.68-1.04-.71.08-.7.08-.7 1.15.08 1.75 1.18 1.75 1.18 1.02 1.75 2.69 1.25 3.34.95.1-.74.4-1.25.72-1.54-2.55-.29-5.24-1.28-5.24-5.69 0-1.25.45-2.28 1.18-3.08-.12-.29-.51-1.46.11-3.04 0 0 .97-.31 3.18 1.18a11.05 11.05 0 0 1 5.79 0c2.21-1.5 3.18-1.18 3.18-1.18.63 1.58.23 2.75.11 3.04.74.8 1.18 1.83 1.18 3.08 0 4.42-2.69 5.39-5.25 5.68.41.36.78 1.07.78 2.17v3.22c0 .31.21.68.8.56C20.21 21.38 23.5 17.07 23.5 12 23.5 5.65 18.35.5 12 .5z" />
      </svg>
   );
}
function Globe({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <circle cx="12" cy="12" r="10" />
         <line x1="2" y1="12" x2="22" y2="12" />
         <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
   );
}
function ArrowRight({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <line x1="5" y1="12" x2="19" y2="12" />
         <polyline points="13 6 19 12 13 18" />
      </svg>
   );
}
function ChatBubble({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      </svg>
   );
}
function SearchIcon({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <circle cx="11" cy="11" r="7" />
         <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
   );
}
function ChatSpark({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
         <path d="M12 8.5l.8 1.7 1.7.8-1.7.8-.8 1.7-.8-1.7-1.7-.8 1.7-.8z" fill="currentColor" stroke="none" />
      </svg>
   );
}
function Code2({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <polyline points="9 9 5 12 9 15" />
         <polyline points="15 9 19 12 15 15" />
      </svg>
   );
}
function Network({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <rect x="9" y="3" width="6" height="5" rx="1.2" />
         <rect x="3" y="16" width="6" height="5" rx="1.2" />
         <rect x="15" y="16" width="6" height="5" rx="1.2" />
         <path d="M12 8v3M6 16v-2h12v2" />
      </svg>
   );
}
function Moon({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
      </svg>
   );
}
function Sun({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <circle cx="12" cy="12" r="4" />
         <line x1="12" y1="2" x2="12" y2="5" />
         <line x1="12" y1="19" x2="12" y2="22" />
         <line x1="2" y1="12" x2="5" y2="12" />
         <line x1="19" y1="12" x2="22" y2="12" />
         <line x1="4.9" y1="4.9" x2="6.8" y2="6.8" />
         <line x1="17.2" y1="17.2" x2="19.1" y2="19.1" />
         <line x1="4.9" y1="19.1" x2="6.8" y2="17.2" />
         <line x1="17.2" y1="6.8" x2="19.1" y2="4.9" />
      </svg>
   );
}
function Person({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
         <circle cx="12" cy="7" r="4" />
      </svg>
   );
}
function Star({ className }) {
   return (
      <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
         <polygon points="12 2 15.1 8.3 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 8.9 8.3 12 2" />
      </svg>
   );
}
function renderIcon(name) {
   switch (name) {
      case "briefcase":
         return <Briefcase />;
      case "code":
         return <CodeIcon />;
      case "js":
         return <span>JS</span>;
      case "spark":
         return <Spark />;
      case "person":
         return <Person />;
      case "star":
         return <Star />;
      default:
         return null;
   }
}

function AiAssistant() {
   const { theme, toggleTheme } = useTheme() || {};
   const [messages, setMessages] = useState([
      {
         id: "welcome",
         role: "assistant",
         text: ASSISTANT_GREETING,
         time: new Date(),
         isWelcome: true,
      },
   ]);
   const [input, setInput] = useState("");
   const [isLoading, setIsLoading] = useState(false);
   const [isDownloading, setIsDownloading] = useState(false);
   const scrollRef = useRef(null);

   const handleDownloadResume = async () => {
      try {
         setIsDownloading(true);
         const fileName = "SunilRawatResume2025.pdf";
         const response = await fetch(
            `${process.env.REACT_APP_SIGNED_API_URL}?fileName=${fileName}`
         );
         const data = await response.json();
         const signedUrl = await data.result;
         const fileResponse = await fetch(signedUrl);

         if (!fileResponse.ok) {
            throw new Error(
               `File download failed with status ${fileResponse.status}`
            );
         }

         const blob = await fileResponse.blob();
         const url = URL.createObjectURL(blob);
         const link = document.createElement("a");
         link.href = url;
         link.download = fileName;
         link.style.display = "none";

         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         URL.revokeObjectURL(url);
      } catch (error) {
         console.error("Error downloading file:", error);
      }
      setIsDownloading(false);
   };

   useEffect(() => {
      if (scrollRef.current) {
         scrollRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
         });
      }
   }, [messages, isLoading]);

   const updateAssistantMessage = (assistantId, patch) => {
      setMessages((prev) =>
         prev.map((m) => (m.id === assistantId ? { ...m, ...patch } : m))
      );
   };

   const sendQuestion = async (question) => {
      const trimmed = question.trim();
      if (!trimmed || isLoading) return;

      const assistantId = `assistant-${Date.now()}`;

      setMessages((prev) => [
         ...prev,
         {
            id: `user-${Date.now()}`,
            role: "user",
            text: trimmed,
            time: new Date(),
         },
         {
            id: assistantId,
            role: "assistant",
            text: "",
            time: new Date(),
            isStreaming: true,
         },
      ]);

      setIsLoading(true);

      try {
         const response = await fetch(AI_ASSISTANT_API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
               question: trimmed,
               assistantType: "portfolio",
               stream: true,
            }),
         });

         if (!response.ok) {
            const rawBody = await response.text();
            const parsed = parseMaybeJson(rawBody);
            throw new Error(
               extractAnswer(parsed) ||
                  `Request failed with status ${response.status}`
            );
         }

         let accumulated = "";

         await consumeStreamingResponse(response, (event) => {
            if (event.type === "status") {
               updateAssistantMessage(assistantId, {
                  streamStatus: event.status,
               });
               return;
            }

            if (event.type !== "text" || !event.text) return;

            accumulated += event.text;
            updateAssistantMessage(assistantId, {
               text: accumulated,
               streamStatus: undefined,
            });
         });

         if (!accumulated.trim()) {
            updateAssistantMessage(assistantId, {
               text: "I could not generate a response right now.",
            });
         }
      } catch (error) {
         const msg = String(error?.message || "").trim();
         const networkErr =
            msg.toLowerCase().includes("failed to fetch") ||
            msg.toLowerCase().includes("networkerror");

         updateAssistantMessage(assistantId, {
            text: networkErr
               ? "I could not reach the AI server. Please try again."
               : msg ||
                 "Sorry, I am unable to respond right now. Please try again.",
            isError: true,
            isStreaming: false,
         });
      } finally {
         updateAssistantMessage(assistantId, {
            isStreaming: false,
            streamStatus: undefined,
         });
         setIsLoading(false);
      }
   };

   const handleSubmit = (e) => {
      e.preventDefault();
      const value = input;
      setInput("");
      sendQuestion(value);
   };

   const hasStreamingMessage = messages.some((m) => m.isStreaming);

   return (
      <main className="aip-page">
         <div className="aip-shell">
            {/* ============= SIDEBAR ============= */}
            <aside className="aip-sidebar">
               <div className="aip-brand">
                  <div className="aip-brand-row">
                     <span className="aip-brand-icon">
                        <Spark />
                     </span>
                     <h1 className="aip-brand-title">AI Assistant</h1>
                     <span className="aip-dot" aria-hidden="true" />
                  </div>
               </div>

               <div className="aip-profile">
                  <div className="aip-profile-avatar-wrap">
                     <span className="aip-profile-avatar-ring">
                        <img
                           className="aip-profile-avatar"
                           src={PORTFOLIO_OWNER_AVATAR}
                           alt={PROFILE.name}
                        />
                     </span>
                  </div>
                  <p className="aip-profile-name">{PROFILE.name}</p>
                  <p className="aip-profile-role">{PROFILE.role}</p>
                  <p className="aip-profile-bio">{PROFILE.bio}</p>

                  <div className="aip-profile-socials">
                     <a
                        className="aip-profile-social aip-profile-social--linkedin"
                        href={PROFILE.linkedin}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label="LinkedIn"
                     >
                        <LinkedIn />
                     </a>
                     <a
                        className="aip-profile-social aip-profile-social--github"
                        href={PROFILE.github}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label="GitHub"
                     >
                        <Github />
                     </a>
                     <a
                        className="aip-profile-social aip-profile-social--web"
                        href={PORTFOLIO_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label="Portfolio website"
                     >
                        <Globe />
                     </a>
                  </div>
               </div>

               <div className="aip-capabilities">
                  <p className="aip-capabilities-title">
                     <span className="aip-capabilities-title-spark" aria-hidden="true">
                        <Spark />
                     </span>
                     <span className="aip-capabilities-title-accent">AI Capabilities</span>
                  </p>

                  <ul className="aip-capabilities-list">
                     {AI_CAPABILITIES.map((c) => (
                        <li
                           key={c.id}
                           className={`aip-cap-item aip-cap-item--${c.color}`}
                        >
                           <span className="aip-cap-icon" aria-hidden="true">
                              {c.icon === "chat" && <ChatBubble />}
                              {c.icon === "search" && <SearchIcon />}
                              {c.icon === "chatspark" && <ChatSpark />}
                              {c.icon === "code2" && <Code2 />}
                              {c.icon === "network" && <Network />}
                           </span>
                           <div className="aip-cap-text">
                              <p className="aip-cap-title">{c.title}</p>
                              <p className="aip-cap-desc">{c.desc}</p>
                           </div>
                        </li>
                     ))}
                  </ul>
               </div>

               <button
                  type="button"
                  className="aip-capabilities-cta"
                  onClick={handleDownloadResume}
                  disabled={isDownloading}
                  aria-busy={isDownloading}
               >
                  {isDownloading ? (
                     <>
                        <span
                           className="aip-capabilities-cta-spinner"
                           aria-hidden="true"
                        />
                        <span>Downloading…</span>
                     </>
                  ) : (
                     <>
                        <Download />
                        <span>Download Resume</span>
                     </>
                  )}
               </button>

            </aside>

            {/* ============= MAIN ============= */}
            <section className="aip-main">
               <div className="aip-topbar">
                  <div className="aip-topbar-intro">
                     <h2 className="aip-empty-greeting">
                        Hi! I'm{" "}
                        <span className="aip-empty-greeting-accent">
                           {ASSISTANT_NAME}
                        </span>{" "}
                        <span
                           className="aip-empty-wave"
                           aria-hidden="true"
                        >
                           👋
                        </span>
                     </h2>
                     <p className="aip-empty-subtitle">
                        Ask me anything about Sunil's skills, experience,
                        projects, or background.
                     </p>
                  </div>
                  <div className="aip-topbar-actions">
                     <button
                        type="button"
                        className="aip-topbar-icon"
                        onClick={toggleTheme}
                        aria-label={
                           theme === "dark"
                              ? "Switch to light mode"
                              : "Switch to dark mode"
                        }
                     >
                        {theme === "dark" ? <Moon /> : <Sun />}
                     </button>
                  </div>
               </div>

               <div className="aip-quick-chips aip-quick-chips--persistent">
                  {QUICK_CHIPS.map((q) => (
                     <button
                        key={q.id}
                        type="button"
                        className={`aip-quick-chip aip-quick-chip--${q.color}`}
                        onClick={() => sendQuestion(q.label)}
                        disabled={isLoading}
                     >
                        <span
                           className="aip-quick-chip-dots"
                           aria-hidden="true"
                        />
                        <span className="aip-quick-chip-icon">
                           {renderIcon(q.icon)}
                        </span>
                        <span className="aip-quick-chip-label">{q.label}</span>
                        <span className="aip-quick-chip-desc">
                           {q.description}
                        </span>
                        <span
                           className="aip-quick-chip-arrow"
                           aria-hidden="true"
                        >
                           <ArrowRight />
                        </span>
                     </button>
                  ))}
               </div>

               <div className="aip-conversation">
                  {messages
                     .filter((m) => !m.isWelcome)
                     .map((m) => {
                     if (m.role === "assistant") {
                        return (
                           <div
                              key={m.id}
                              className="aip-msg-row aip-msg-row--assistant"
                           >
                              <img
                                 className="aip-msg-avatar"
                                 src={ASSISTANT_AVATAR}
                                 alt={`${ASSISTANT_NAME} avatar`}
                              />
                              <div className="aip-msg-stack">
                                 <div
                                    className={`aip-bubble ${
                                       m.isError ? "aip-bubble--error" : ""
                                    }${m.isStreaming ? " aip-bubble--streaming" : ""}`}
                                 >
                                    {m.isStreaming && !m.text ? (
                                       formatStreamStatus(m.streamStatus) ? (
                                          <span
                                             className="aip-stream-status"
                                             aria-live="polite"
                                          >
                                             {formatStreamStatus(m.streamStatus)}
                                          </span>
                                       ) : (
                                          <span
                                             className="aip-typing"
                                             aria-label="Assistant is typing"
                                          >
                                             <span />
                                             <span />
                                             <span />
                                          </span>
                                       )
                                    ) : (
                                       <div className="aip-bubble-markdown-wrap">
                                          <AssistantMarkdown text={m.text} />
                                          {m.isStreaming ? (
                                             <span
                                                className="aip-stream-cursor"
                                                aria-hidden="true"
                                             />
                                          ) : null}
                                       </div>
                                    )}
                                 </div>
                              </div>
                           </div>
                        );
                     }
                     return (
                        <div
                           key={m.id}
                           className="aip-msg-row aip-msg-row--user"
                        >
                           <div className="aip-msg-stack aip-msg-stack--user">
                              <div className="aip-bubble aip-bubble--user">
                                 {m.text}
                              </div>
                           </div>
                        </div>
                     );
                  })}

                  {isLoading && !hasStreamingMessage && (
                     <div className="aip-msg-row aip-msg-row--assistant">
                        <img
                           className="aip-msg-avatar"
                           src={ASSISTANT_AVATAR}
                           alt={`${ASSISTANT_NAME} avatar`}
                        />
                        <div className="aip-bubble aip-bubble--typing">
                           <span
                              className="aip-typing"
                              aria-label="Assistant is typing"
                           >
                              <span />
                              <span />
                              <span />
                           </span>
                        </div>
                     </div>
                  )}

                  <div ref={scrollRef} />
               </div>

               <div className="aip-input-area">
                  <form className="aip-input-form" onSubmit={handleSubmit}>
                     <input
                        type="text"
                        className="aip-input"
                        placeholder="Ask anything..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        disabled={isLoading}
                        aria-label="Ask a question"
                     />
                     <button
                        type="submit"
                        className="aip-send"
                        disabled={isLoading || !input.trim()}
                        aria-label="Send message"
                     >
                        <Send />
                     </button>
                  </form>
                  <p className="aip-footnote">
                     <span aria-hidden="true">🔒</span>
                     AI responses are based on my portfolio data.
                  </p>
               </div>
            </section>
         </div>
      </main>
   );
}

export default AiAssistant;
